/**
 * DOM Introspection Layer (DIL)
 * Captures visible DOM elements with bounding boxes for accurate element targeting
 * Sends snapshots to Pipecat server for vision + DOM correlation
 */

import { DOMSnapshot, DOMElementInfo, isSDKWidget, getSanitizedUrl } from './types';
import { logger as defaultLogger, Logger } from './logger';
import {
  invalidateModalCache,
  isAriaHidden,
  isAllowedByModals,
  isInsideCollapsedDetails,
  isInsideCollapsedAccordion,
  checkInteractable,
} from './visibility-utils';

export class DOMIntrospector {
  private readonly logger: Logger;
  private lastSnapshot: DOMSnapshot | null = null;
  private lastSnapshotHash: string = '';
  private snapshotInterval: number | null = null;
  private sendCallback: ((snapshot: DOMSnapshot) => void) | null = null;
  private correlationCounter: number = 0;  // For tracking snapshots across client/server
  private lastSendTimestamp: number = 0;  // For heartbeat timing to prevent server staleness
  private readonly INTERACTIVE_TAGS = new Set([
    'button', 'a', 'input', 'select', 'textarea', 'label',
    'summary', 'details', 'option', 'menuitem'
  ]);
  private readonly INTERACTIVE_ROLES = new Set([
    'button', 'link', 'menuitem', 'option', 'tab', 'checkbox',
    'radio', 'switch', 'textbox', 'combobox', 'listbox', 'slider'
  ]);

  // MutationObserver that invalidates the shared modal cache on relevant DOM changes
  private _visibilityCacheObserver: MutationObserver | null = null;

  // --- capture coalescing (see scheduleCapture) ---
  private _coalesceTimer: ReturnType<typeof setTimeout> | null = null;
  private _pendingReason: string | null = null;
  private _lastCaptureAt: number = 0;
  private _burstStartedAt: number = 0;

  constructor(logger: Logger = defaultLogger) {
    this.logger = logger;
  }

  /**
   * Start periodic DOM snapshots
   * @param intervalMs How often to capture (default 1500ms)
   * @param sendCallback Function to send snapshot to server
   */
  startCapturing(intervalMs: number = 1500, sendCallback: (snapshot: DOMSnapshot) => void) {
    this.logger.log(`[DOMIntrospector] Starting DOM capture every ${intervalMs}ms`);

    // Guard against duplicate timers/observers — clean up any existing capture
    // before starting a new one so we never leak concurrent intervals or observers.
    this.cancelScheduledCapture();
    if (this.snapshotInterval) {
      clearInterval(this.snapshotInterval);
      this.snapshotInterval = null;
    }
    if (this.mutationDebounceTimer) {
      clearTimeout(this.mutationDebounceTimer);
      this.mutationDebounceTimer = null;
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    if (this._visibilityCacheObserver) {
      this._visibilityCacheObserver.disconnect();
      this._visibilityCacheObserver = null;
    }

    this.sendCallback = sendCallback;

    // Initialize visibility cache invalidation observer
    this.initCacheInvalidation();

    // Capture immediately
    this.captureAndSend();

    // Then capture periodically
    this.snapshotInterval = window.setInterval(() => {
      this.captureAndSend();
    }, intervalMs);

    // Also capture on significant DOM changes (debounced)
    this.setupMutationObserver();
  }

  /**
   * Stop capturing
   */
  stopCapturing() {
    this.cancelScheduledCapture();
    if (this.snapshotInterval) {
      clearInterval(this.snapshotInterval);
      this.snapshotInterval = null;
    }
    // Clear any pending mutation debounce timer to prevent capture-after-stop
    if (this.mutationDebounceTimer) {
      clearTimeout(this.mutationDebounceTimer);
      this.mutationDebounceTimer = null;
    }
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
    if (this._visibilityCacheObserver) {
      this._visibilityCacheObserver.disconnect();
      this._visibilityCacheObserver = null;
    }
    this.logger.log('[DOMIntrospector] Stopped DOM capture');
  }

  /**
   * Request a capture, collapsing a burst of requests into ONE.
   *
   * This is the entry point every event-driven trigger should use.
   * `captureAndSendImmediate` remains the uncoalesced primitive underneath.
   *
   * Why this exists: a single bot click used to fan out to 4-6 full captures
   * inside about a second — `post-scroll` from the scrollIntoView, `post-click`
   * from the SDK's own document listener seeing its own synthetic click,
   * `post-bot-click` from the action handler, `dom-mutation` from the host
   * re-rendering (and from the SDK's own highlight nodes), plus `post-navigation`
   * — on top of the 1500ms metronome. Each of those is *two* whole-document
   * walks (DOM + accessible snapshot), synchronous, on the host's main thread.
   * Nothing coordinated them, so the cost landed exactly when the user was
   * watching the bot work. That is the stutter.
   *
   * They are all asking the same question — "what does the page look like now?" —
   * and only the last answer matters, so a trailing-edge timer with a floor on
   * capture frequency answers it once.
   *
   * Trailing rather than leading edge on purpose: the point of a post-action
   * capture is to observe the state the action SETTLED into, and the callers
   * already delay 100-500ms for exactly that reason.
   *
   * @param reason Reason for the capture (for logging); the newest one wins
   */
  scheduleCapture(reason: string = 'immediate') {
    this._pendingReason = reason;

    const now = Date.now();
    if (this._coalesceTimer === null) {
      this._burstStartedAt = now;
    } else {
      // Rearm: the burst is still going, so the quiet period restarts from THIS
      // request. Without this, a request landing near the end of a fixed window
      // would be captured almost immediately — photographing the page before the
      // change that request was reporting had settled.
      clearTimeout(this._coalesceTimer);
      this._coalesceTimer = null;
    }

    // Quiet period since the last request, but never past the max wait — a pure
    // debounce starves on a page that mutates continuously (a live-updating CRM
    // grid would reset the timer forever and never capture at all).
    const quiet = DOMIntrospector.CAPTURE_COALESCE_MS;
    const maxWaitRemaining = Math.max(
      0,
      this._burstStartedAt + DOMIntrospector.MAX_COALESCE_WAIT_MS - now,
    );
    // The rate-limit floor is separate and always wins: it protects the main
    // thread regardless of how the debounce resolved.
    const gapFloor = Math.max(0, DOMIntrospector.MIN_CAPTURE_GAP_MS - (now - this._lastCaptureAt));
    const delay = Math.max(Math.min(quiet, maxWaitRemaining), gapFloor);

    this._coalesceTimer = setTimeout(() => {
      this._coalesceTimer = null;
      const pending = this._pendingReason ?? 'immediate';
      this._pendingReason = null;
      this.captureAndSendImmediate(pending);
    }, delay);
  }

  /** Drop any pending coalesced capture (used by stopCapturing). */
  private cancelScheduledCapture() {
    if (this._coalesceTimer !== null) {
      clearTimeout(this._coalesceTimer);
      this._coalesceTimer = null;
    }
    this._pendingReason = null;
  }

  /** Quiet period: how long after the LAST request in a burst to wait. */
  private static readonly CAPTURE_COALESCE_MS = 250;

  /**
   * Ceiling on how long one burst can defer its capture, measured from the first
   * request. Bounds the debounce so a continuously-mutating page still gets
   * captured instead of starving.
   */
  private static readonly MAX_COALESCE_WAIT_MS = 1000;

  /**
   * Floor on time between captures. A burst that arrives just after a capture
   * waits out the remainder of this rather than paying twice in a row.
   */
  private static readonly MIN_CAPTURE_GAP_MS = 750;

  /**
   * Capture and send immediately (bypasses hash check)
   * Used for post-action synchronization to ensure server has fresh DOM state.
   *
   * Prefer `scheduleCapture` for event-driven triggers — this one is
   * uncoalesced and runs two full-document walks synchronously.
   *
   * @param reason Optional reason for the immediate capture (for logging)
   */
  captureAndSendImmediate(reason: string = 'immediate') {
    this._lastCaptureAt = Date.now();
    this.logger.log(`[DOMIntrospector] Immediate capture triggered: ${reason}`);
    const snapshot = this.captureSnapshot();
    const newHash = this.computeSnapshotHash(snapshot);

    // Check if DOM actually changed (even for immediate captures)
    const domChanged = newHash !== this.lastSnapshotHash;

    // Add correlation ID and capture reason for server-side tracking
    const correlatedSnapshot: DOMSnapshot = {
      ...snapshot,
      correlationId: `dom-${Date.now()}-${++this.correlationCounter}`,
      captureReason: reason,
      domChanged: domChanged  // Always true for immediate captures that changed, helps server decide on vision
    };

    // Update local cache
    this.lastSnapshot = correlatedSnapshot;
    this.lastSnapshotHash = newHash;

    if (this.sendCallback) {
      this.logger.log(`[DOMIntrospector] Sending immediate snapshot (changed=${domChanged}): ${snapshot.elements.length} elements, corr=${correlatedSnapshot.correlationId}`);
      this.sendCallback(correlatedSnapshot);
    }

    return correlatedSnapshot.correlationId;
  }

  /**
   * Setup mutation observer for significant DOM changes
   */
  private mutationObserver: MutationObserver | null = null;
  private mutationDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  private setupMutationObserver() {
    if (this.mutationObserver) return;

    this.mutationObserver = new MutationObserver((mutations) => {
      // Debounce mutation handling
      if (this.mutationDebounceTimer) {
        clearTimeout(this.mutationDebounceTimer);
      }

      // Check if mutations are significant (not just style/class changes)
      const significantMutation = mutations.some(m =>
        m.type === 'childList' ||
        (m.type === 'attributes' && [
          'href', 'src', 'hidden', 'disabled',
          'aria-hidden', 'aria-modal', 'aria-expanded', 'open'
        ].includes(m.attributeName || ''))
      );

      if (significantMutation) {
        this.mutationDebounceTimer = setTimeout(() => {
          this.logger.log('[DOMIntrospector] Significant DOM change detected');
          // scheduleCapture rather than captureAndSend so the snapshot is always
          // sent after a significant mutation (e.g. a modal opening) — the regular
          // captureAndSend skips on an unchanged hash, which can miss newly added
          // elements that don't affect the sampled hash — and rather than
          // captureAndSendImmediate so a host re-render that also triggered a
          // click/scroll capture is not walked twice.
          this.scheduleCapture('dom-mutation');
        }, 300);
      }
    });

    this.mutationObserver.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['hidden', 'disabled', 'aria-hidden', 'aria-modal', 'aria-expanded', 'open', 'href', 'src']
    });
  }

  /**
   * Capture DOM snapshot and send if changed
   */
  private captureAndSend() {
    // Stamp the clock here too, not only in captureAndSendImmediate: this is a
    // full DOM walk like any other, so MIN_CAPTURE_GAP_MS must count from it.
    // Otherwise the 1500ms metronome could fire and a scheduled capture land
    // microseconds later, paying for two whole-document passes back to back —
    // exactly the doubling the coalescer exists to prevent.
    this._lastCaptureAt = Date.now();
    const snapshot = this.captureSnapshot();
    const hash = this.computeSnapshotHash(snapshot);

    // Check if DOM actually changed
    const domChanged = hash !== this.lastSnapshotHash;

    // Send if DOM changed, or send a heartbeat every 10s to prevent server staleness
    const now = Date.now();
    const timeSinceLastSend = now - (this.lastSendTimestamp || 0);
    const shouldHeartbeat = !domChanged && timeSinceLastSend > 10000;

    if (domChanged || shouldHeartbeat) {
      // Add correlation ID and domChanged flag for server-side tracking
      const correlatedSnapshot: DOMSnapshot = {
        ...snapshot,
        correlationId: `dom-${Date.now()}-${++this.correlationCounter}`,
        captureReason: domChanged ? 'periodic' : 'heartbeat',
        domChanged  // Signal server whether vision analysis may be needed
      };

      this.lastSnapshot = correlatedSnapshot;
      if (domChanged) {
        this.lastSnapshotHash = hash;
      }
      this.lastSendTimestamp = now;

      if (this.sendCallback) {
        this.logger.log(`[DOMIntrospector] Sending snapshot (${domChanged ? 'DOM changed' : 'heartbeat'}): ${snapshot.elements.length} elements, ${snapshot.interactiveCount} interactive, corr=${correlatedSnapshot.correlationId}`);
        this.sendCallback(correlatedSnapshot);
      }
    } else {
      this.logger.log('[DOMIntrospector] Snapshot unchanged, skipping send');
    }
  }

  /**
   * Compute hash of snapshot for change detection
   */
  private computeSnapshotHash(snapshot: DOMSnapshot): string {
    // Hash based on element count + sampled element positions and text
    // IMPORTANT: Include total element count so that adding/removing elements
    // (e.g., opening a modal) always produces a different hash, even if the
    // first elements remain the same.
    const totalCount = snapshot.elements.length;
    const interactiveCount = snapshot.interactiveCount || 0;

    // Sample elements from beginning, middle, and end for a representative hash
    const sampleSize = 30;
    const beginSample = snapshot.elements.slice(0, sampleSize);
    const midStart = Math.max(0, Math.floor(totalCount / 2) - Math.floor(sampleSize / 2));
    const midSample = snapshot.elements.slice(midStart, midStart + sampleSize);
    const endSample = snapshot.elements.slice(-sampleSize);

    const hashElements = (els: typeof snapshot.elements) =>
      els.map(e => `${e.tag}:${e.text?.slice(0, 20)}:${Math.round(e.rect.x)}:${Math.round(e.rect.y)}`).join('|');

    const sig = `count:${totalCount}:${interactiveCount}|` +
      hashElements(beginSample) + '||' +
      hashElements(midSample) + '||' +
      hashElements(endSample);
    return sig;
  }

  /**
   * Capture current visible DOM state
   */
  captureSnapshot(): DOMSnapshot {
    const elements: DOMElementInfo[] = [];
    const viewport = {
      width: window.innerWidth,
      height: window.innerHeight
    };

    // Query all potentially relevant elements
    const allElements = document.body.querySelectorAll('*');
    let index = 0;
    let interactiveCount = 0;

    allElements.forEach((el) => {
      const htmlEl = el as HTMLElement;

      // Skip invisible and our own elements
      if (this.shouldSkipElement(htmlEl)) return;

      const rect = htmlEl.getBoundingClientRect();

      // Skip elements outside viewport
      if (rect.bottom < 0 || rect.top > viewport.height ||
        rect.right < 0 || rect.left > viewport.width) {
        return;
      }

      // Skip elements with no size
      if (rect.width <= 0 || rect.height <= 0) return;

      const isInteractive = this.isInteractiveElement(htmlEl);
      if (isInteractive) interactiveCount++;

      // Only include interactive elements or elements with text
      const text = this.getVisibleText(htmlEl);
      if (!isInteractive && !text) return;

      const elementInfo: DOMElementInfo = {
        index: index++,
        tag: htmlEl.tagName.toLowerCase(),
        text: text,
        id: htmlEl.id || '',
        className: typeof htmlEl.className === 'string' ? htmlEl.className : '',
        ariaLabel: htmlEl.getAttribute('aria-label'),
        placeholder: htmlEl.getAttribute('placeholder'),
        role: htmlEl.getAttribute('role'),
        href: htmlEl.getAttribute('href'),
        type: htmlEl.getAttribute('type'),
        name: htmlEl.getAttribute('name'),
        rect: {
          x: Math.round(rect.x),
          y: Math.round(rect.y),
          width: Math.round(rect.width),
          height: Math.round(rect.height)
        },
        isVisible: true,
        isInteractive: isInteractive,
        isInteractable: isInteractive ? checkInteractable(htmlEl, true) : false,
        parentTag: htmlEl.parentElement?.tagName.toLowerCase() || null,
        depth: this.getElementDepth(htmlEl)
      };

      elements.push(elementInfo);
    });

    return {
      timestamp: Date.now(),
      url: getSanitizedUrl(),
      viewport,
      elements,
      totalElements: elements.length,
      interactiveCount
    };
  }

  /**
   * Check if element should be skipped from DOM captures
   */
  private shouldSkipElement(el: HTMLElement): boolean {
    // Skip our own SDK widget elements (overlays, cursor, highlights, etc.)
    if (isSDKWidget(el)) {
      return true;
    }

    // Skip script, style, and hidden elements
    const tag = el.tagName.toLowerCase();
    if (['script', 'style', 'noscript', 'template', 'svg', 'path'].includes(tag)) {
      return true;
    }

    // Check computed visibility
    const style = window.getComputedStyle(el);
    if (style.display === 'none' ||
      style.visibility === 'hidden' ||
      style.opacity === '0') {
      return true;
    }

    // Skip elements hidden by aria-hidden="true" (e.g., behind modals)
    if (isAriaHidden(el)) return true;

    // Skip elements behind an active modal overlay
    if (!isAllowedByModals(el)) return true;

    // Skip elements inside collapsed <details> (except the <details> and its <summary>)
    if (isInsideCollapsedDetails(el)) return true;

    // Skip elements inside collapsed accordion panels
    if (isInsideCollapsedAccordion(el)) return true;

    return false;
  }

  /**
   * Initialize a MutationObserver that invalidates the shared modal cache
   * whenever relevant ARIA/DOM attributes change.
   */
  private initCacheInvalidation() {
    if (this._visibilityCacheObserver) {
      this._visibilityCacheObserver.disconnect();
    }

    this._visibilityCacheObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' || mutation.type === 'childList') {
          invalidateModalCache();
          break;
        }
      }
    });

    // Only watch ARIA/semantic attributes that affect modal and accordion state.
    // Deliberately excludes 'style' and 'class' to avoid thrashing the cache on
    // pages with CSS animations or frequent React re-renders. The 500ms TTL
    // fallback in getActiveModals() handles style-based visibility changes.
    this._visibilityCacheObserver.observe(document.body, {
      attributes: true,
      attributeFilter: ['aria-modal', 'aria-hidden', 'aria-expanded', 'open'],
      childList: true,
      subtree: true,
    });
  }

  /**
   * Check if element is interactive
   */
  private isInteractiveElement(el: HTMLElement): boolean {
    const tag = el.tagName.toLowerCase();

    // Check tag
    if (this.INTERACTIVE_TAGS.has(tag)) return true;

    // Check role
    const role = el.getAttribute('role');
    if (role && this.INTERACTIVE_ROLES.has(role)) return true;

    // Check for click handlers (heuristic)
    if (el.onclick || el.getAttribute('onclick')) return true;

    // Check for contenteditable
    if (el.contentEditable === 'true') return true;

    // Check for tabindex
    const tabIndex = el.getAttribute('tabindex');
    if (tabIndex && parseInt(tabIndex) >= 0) return true;

    // Check cursor style
    const style = window.getComputedStyle(el);
    if (style.cursor === 'pointer') return true;

    return false;
  }

  /**
   * Get visible text content (truncated)
   */
  private getVisibleText(el: HTMLElement): string {
    // For inputs, get value or placeholder
    if (el instanceof HTMLInputElement) {
      return (el.value || el.placeholder || '').trim().slice(0, 80);
    }
    if (el instanceof HTMLTextAreaElement) {
      return (el.value || el.placeholder || '').trim().slice(0, 80);
    }

    // For other elements, get innerText but only direct text (not children)
    const text = el.innerText?.trim() || '';
    return text.slice(0, 80);
  }

  /**
   * Get element depth in DOM tree
   */
  private getElementDepth(el: HTMLElement): number {
    let depth = 0;
    let current: HTMLElement | null = el;
    while (current && current !== document.body) {
      depth++;
      current = current.parentElement;
    }
    return depth;
  }

  /**
   * Get current snapshot without triggering a new capture
   */
  getLastSnapshot(): DOMSnapshot | null {
    return this.lastSnapshot;
  }

  /**
   * Find element by DOM index (fast lookup from server response)
   */
  findElementByIndex(domIndex: number): HTMLElement | null {
    if (!this.lastSnapshot) return null;

    const elementInfo = this.lastSnapshot.elements.find(e => e.index === domIndex);
    if (!elementInfo) return null;

    // Use bounding box to find the actual element
    return this.findElementByBoundingBox(elementInfo.rect, elementInfo.tag, elementInfo.text);
  }

  /**
   * Find element by bounding box (primary DIL strategy)
   * 
   * CRITICAL: If expectedText is provided, elements MUST match the text.
   * This prevents clicking wrong elements when the page has scrolled/animated.
   */
  findElementByBoundingBox(
    targetRect: { x: number; y: number; width: number; height: number },
    expectedTag?: string,
    expectedText?: string
  ): HTMLElement | null {
    const tolerance = 10; // pixels
    const candidates: { element: HTMLElement; score: number; textMatch: boolean }[] = [];

    const allElements = document.body.querySelectorAll('*');

    allElements.forEach((el) => {
      const htmlEl = el as HTMLElement;
      if (this.shouldSkipElement(htmlEl)) return;

      const rect = htmlEl.getBoundingClientRect();

      // Check if bounding box overlaps
      const xOverlap = Math.abs(rect.x - targetRect.x) <= tolerance;
      const yOverlap = Math.abs(rect.y - targetRect.y) <= tolerance;
      const widthMatch = Math.abs(rect.width - targetRect.width) <= tolerance * 2;
      const heightMatch = Math.abs(rect.height - targetRect.height) <= tolerance * 2;

      if (xOverlap && yOverlap && widthMatch && heightMatch) {
        let score = 100;
        let textMatch = false;

        // Boost score for tag match
        if (expectedTag && htmlEl.tagName.toLowerCase() === expectedTag.toLowerCase()) {
          score += 30;
        }

        // Check text match - CRITICAL for avoiding wrong element clicks
        if (expectedText) {
          const elementText = htmlEl.innerText?.trim().toLowerCase() || '';
          const targetText = expectedText.toLowerCase();

          if (elementText.length === 0) {
            // Empty element text cannot satisfy a non-empty expected text.
            // Without this guard, targetText.includes('') would return true
            // and bypass the CRITICAL text-matching safeguard.
            textMatch = false;
          } else if (elementText === targetText) {
            score += 50;
            textMatch = true;
          } else if (elementText.includes(targetText) || targetText.includes(elementText)) {
            score += 25;
            textMatch = true;
          }
        } else {
          // No expected text means we can't verify, treat as match
          textMatch = true;
        }

        candidates.push({ element: htmlEl, score, textMatch });
      }
    });

    // Sort by score and return best match
    candidates.sort((a, b) => b.score - a.score);

    if (candidates.length > 0) {
      const best = candidates[0];
      const foundText = best.element.innerText?.trim().slice(0, 50) || '';

      // CRITICAL: If expectedText was provided, REQUIRE text match
      // This prevents clicking "Collectors" when looking for "Cloud Accounts"
      if (expectedText && !best.textMatch) {
        this.logger.warn(
          `[DOMIntrospector] ⚠️ Bounding box match REJECTED - text mismatch! ` +
          `Expected: "${expectedText}", Found: "${foundText}"`
        );
        // Log what we found at the position for debugging
        this.logger.warn(
          `[DOMIntrospector] Element at (${targetRect.x}, ${targetRect.y}) has text "${foundText}" ` +
          `which doesn't match expected "${expectedText}" - page may have scrolled`
        );
        return null;
      }

      this.logger.log(
        `[DOMIntrospector] Found ${candidates.length} candidates by bounding box, ` +
        `best score: ${best.score}, text: "${foundText}"`
      );
      return best.element;
    }

    return null;
  }
}
